/**
 * useFaceCapture — live webcam → face pose + blendshapes, in-browser, on a
 * plain camera. MediaPipe FaceLandmarker (WASM/GPU) runs each frame and writes
 * into faceBridge: head pose (yaw/pitch/roll) drives the character's Head bone
 * NOW; the ARKit-52 blendshapes are captured and exposed for a future face rig.
 *
 * This is the "motion as data" spike — the performer's own head drives the
 * avatar live (~a few landmarks of latency), and the same tiny pose payload is
 * what you'd broadcast to viewers instead of a video stream.
 *
 * Secure context required (getUserMedia) — the dev server is HTTPS. Desktop/GPU
 * first; phones run it slower.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { faceBridge } from '../state/faceBridge';

const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

// Mirror + gain so it reads like driving the avatar with your own head. Tune
// live with ?facegain=1.2 ; signs chosen so turning your head right turns his.
const YAW_SIGN = -1, PITCH_SIGN = 1, ROLL_SIGN = -1;
const CLAMP = 0.7; // rad — keep the neck from snapping past a natural range

// ── THE FRAME ARRIVES SIDEWAYS ON A PHONE ───────────────────────────────────
//
// Khari, 2026-08-07, on mobile Safari: "When I open the camera, it is 90deg
// rotated, making it hard to map to my features."
//
// He is describing the cause, not a symptom. iOS hands back a LANDSCAPE track
// from a portrait phone — the sensor's own orientation — and the <video>
// element paints it upright using rotation metadata that `detectForVideo` never
// sees. So the model is looking at a face lying on its side, and FaceLandmarker
// does not rotate anything for you: detection either fails outright or returns a
// pose whose axes are swapped, which is exactly "it won't map to my features".
//
// So the frames get straightened before detection, in a canvas the size of the
// UPRIGHT image. Costs one draw per frame and nothing else — no extra model
// work, since the landmarker is happy to take a canvas.
//
// WHICH WAY TO TURN IT is the part worth being able to change without a build:
// it depends on the device, the facing mode and how the browser reports
// orientation. `?facerot=` forces it (0 | 90 | 180 | 270), the same live-tuning
// convention as ?facegain and the lamp rig.
function forcedRotation() {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search).get('facerot');
  if (q === null) return null;
  const n = parseInt(q, 10);
  return [0, 90, 180, 270].includes(n) ? n : null;
}

/**
 * How far to turn the frame so the face stands upright.
 *
 * Only ever non-zero when the TRACK and the SCREEN disagree about which way is
 * long — a landscape frame on a portrait screen. When they agree, the frame is
 * already right and this stays out of the way (every desktop webcam, and a
 * phone actually held sideways).
 */
function uprightRotation(vid) {
  const forced = forcedRotation();
  if (forced !== null) return forced;
  if (!vid?.videoWidth || !vid?.videoHeight) return 0;
  const frameIsLandscape = vid.videoWidth > vid.videoHeight;
  const screenIsPortrait = typeof window !== 'undefined' && window.innerHeight >= window.innerWidth;
  if (!frameIsLandscape || !screenIsPortrait) return 0;
  // `screen.orientation.angle` says how the DEVICE is turned from its natural
  // orientation; the frame has to turn back by the same amount. Safari has
  // supported it since 16.4 — older iOS falls back to 90, the common case for a
  // front camera on a phone held upright.
  const angle = (typeof screen !== 'undefined' && screen.orientation?.angle) ?? 0;
  return angle === 0 ? 90 : (360 - angle) % 360;
}

export default function useFaceCapture({ videoRef }) {
  const [status, setStatus] = useState('idle'); // idle | loading | live | error
  const [error, setError] = useState('');
  const landmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(-1);
  const smooth = useRef({ yaw: 0, pitch: 0, roll: 0 });
  // Reused every frame — allocating a canvas per frame is how you turn a
  // rotation fix into a garbage-collection stutter.
  const canvasRef = useRef(null);
  const rotLoggedRef = useRef(false);

  const stop = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    const v = videoRef.current;
    if (v) { try { v.pause(); v.srcObject = null; } catch { /* noop */ } }
    // Say it again next time — turning the phone between sessions changes the
    // answer, and a once-per-process log would report the first orientation
    // forever.
    rotLoggedRef.current = false;
    faceBridge.active = false;
    setStatus('idle');
  }, [videoRef]);

  const start = useCallback(async () => {
    setError(''); setStatus('loading');
    try {
      // Lazy-load the model once; reuse across toggles.
      if (!landmarkerRef.current) {
        const vision = await import('@mediapipe/tasks-vision');
        const fileset = await vision.FilesetResolver.forVisionTasks(WASM_URL);
        landmarkerRef.current = await vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          outputFaceBlendshapes: true,
          outputFacialTransformationMatrixes: true,
          runningMode: 'VIDEO',
          numFaces: 1,
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();

      const gain = parseFloat(new URLSearchParams(window.location.search).get('facegain')) || 1.1;
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const e = new THREE.Euler();
      const pos = new THREE.Vector3();
      const scl = new THREE.Vector3();
      const clampv = (x) => Math.max(-CLAMP, Math.min(CLAMP, x));

      faceBridge.active = true;
      setStatus('live');

      const loop = () => {
        const vid = videoRef.current;
        if (!vid || vid.readyState < 2) { rafRef.current = requestAnimationFrame(loop); return; }
        let ts = performance.now();
        if (ts <= lastTsRef.current) ts = lastTsRef.current + 1; // strictly increasing
        lastTsRef.current = ts;

        // Straighten the frame first when the phone handed us a sideways one.
        // `source` is the video itself on every device that needs no turning, so
        // the desktop path is byte-for-byte what it was.
        const rot = uprightRotation(vid);
        let source = vid;
        if (rot) {
          const swap = rot === 90 || rot === 270;
          const w = swap ? vid.videoHeight : vid.videoWidth;
          const h = swap ? vid.videoWidth : vid.videoHeight;
          let cv = canvasRef.current;
          if (!cv) { cv = document.createElement('canvas'); canvasRef.current = cv; }
          if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
          const ctx = cv.getContext('2d');
          ctx.save();
          // Turn about the CENTRE of the destination, then draw the frame
          // centred on its own middle — otherwise a 90° turn puts the image
          // entirely outside the canvas and the model sees black.
          ctx.translate(w / 2, h / 2);
          ctx.rotate((rot * Math.PI) / 180);
          ctx.drawImage(vid, -vid.videoWidth / 2, -vid.videoHeight / 2);
          ctx.restore();
          source = cv;
          if (!rotLoggedRef.current) {
            rotLoggedRef.current = true;
            console.log(`[face] frame arrived ${vid.videoWidth}×${vid.videoHeight} on a portrait screen — `
              + `straightening ${rot}° before detection (override with ?facerot=0|90|180|270)`);
          }
        }
        const res = landmarkerRef.current.detectForVideo(source, ts);

        const mat = res.facialTransformationMatrixes?.[0]?.data;
        if (mat) {
          m.fromArray(mat);
          m.decompose(pos, q, scl);
          e.setFromQuaternion(q, 'YXZ');
          const tYaw = clampv(YAW_SIGN * e.y * gain);
          const tPitch = clampv(PITCH_SIGN * e.x * gain);
          const tRoll = clampv(ROLL_SIGN * e.z * gain);
          // Light smoothing (One-Euro-lite): lerp toward target.
          const s = smooth.current;
          s.yaw += (tYaw - s.yaw) * 0.4;
          s.pitch += (tPitch - s.pitch) * 0.4;
          s.roll += (tRoll - s.roll) * 0.4;
          faceBridge.yaw = s.yaw; faceBridge.pitch = s.pitch; faceBridge.roll = s.roll;
        }
        const cats = res.faceBlendshapes?.[0]?.categories;
        if (cats) { const b = {}; for (const c of cats) b[c.categoryName] = c.score; faceBridge.blend = b; }

        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch (err) {
      console.error('[FaceCapture] start failed:', err);
      setError(err?.message || 'Face capture failed to start');
      setStatus('error');
      stop();
    }
  }, [videoRef, stop]);

  useEffect(() => () => {
    stop();
    try { landmarkerRef.current?.close?.(); } catch { /* noop */ }
    landmarkerRef.current = null;
  }, [stop]);

  return { status, error, start, stop };
}
